// Central place to tune prompts without touching backend
// Backward-compatible with your previous callers, but supports richer context.

/* -------------------- helpers -------------------- */
const short = v => (v ?? '').toString().trim();
const list = arr => (Array.isArray(arr) && arr.length ? arr.join(', ') : '');
const isDateSoon = iso => {
  if (!iso) return false;
  const d = new Date(iso);
  if (isNaN(d)) return false;
  const diffDays = (d - new Date()) / (1000 * 60 * 60 * 24);
  return diffDays <= 7;
};

const normalizeScope = s => {
  const val = (s || '').toString().toLowerCase();
  if (!val) return '';
  if (val.includes('individual')) return 'Individual Item';
  if (val.includes('line') || val.includes('category')) return 'Product Line';
  if (val.includes('store') || val.includes('storewide') || val === 'all')
    return 'Storewide Promotion';
  return '';
};

/* Optional: very light formatting if callers pass raw types */
const fmtValueAndType = (discountValue, couponType) => {
  const val = short(discountValue);
  const type = short(couponType).toLowerCase();
  if (!val || !type) return `${val} ${couponType}`.trim();
  if (type.includes('percent')) return `${val}% off`;
  if (type.includes('dollar') || type.includes('amount')) return `$${val} off`;
  if (type.includes('points')) return `${val} points`;
  if (type.includes('bogo')) return `BOGO (${val})`;
  if (type.includes('free')) return `FREE (${val})`;
  return `${val} ${couponType}`;
};

/**
 * Defensive sanitizer used inside every build* helper.
 * Ensures a value is both truthy AND non-empty after trimming.
 * Rejects 0, "0", " ", false, [], {}, and any other JS-truthy-but-empty value.
 * Returns the trimmed string if valid, otherwise "".
 */
const clean = val => {
  if (val == null) return '';
  // Arrays: must have at least one non-empty element
  if (Array.isArray(val)) {
    const joined = val.map(v => String(v).trim()).filter(Boolean).join(', ');
    return joined;
  }
  const str = String(val).trim();
  // Reject bare zeros or placeholder-looking values that have no real content
  if (!str || str === '0') return '';
  return str;
};

/**
 * Builds a "FORBIDDEN TOPICS" block listing every absent restriction field.
 * Passing this to the model prevents it from inventing values for fields
 * that were never provided — the root cause of hallucinated times/days/spends.
 */
const buildForbiddenBlock = ({ slowDaysS, timeRangeS, minSpendS, purchaseQtyS, exclusivityContext }) => {
  const days  = clean(slowDaysS);
  const time  = clean(timeRangeS);
  const spend = clean(minSpendS);
  const qty   = clean(purchaseQtyS);

  const forbidden = [];
  if (!days)               forbidden.push('- Valid days or day-of-week restrictions (e.g. "Mondays only", "weekends")');
  if (!time)               forbidden.push('- Time windows or hours of availability (e.g. "11 AM - 3 PM", "after 5 PM")');
  if (!spend)              forbidden.push('- Minimum spend or purchase thresholds (e.g. "spend $25 or more")');
  if (!qty)                forbidden.push('- Purchase quantity requirements (e.g. "buy 2 or more")');
  if (!exclusivityContext) forbidden.push('- Membership, premium, or follower exclusivity');

  if (!forbidden.length) return '';

  return `
FORBIDDEN TOPICS FOR THIS COUPON
=================================
The following data fields were NOT provided. You MUST NOT mention, imply,
invent, or hint at them ANYWHERE in your output — not in the title, description,
or terms. Inventing values for absent fields is a critical failure.

${forbidden.join('\n')}`.trim();
};

/**
 * Builds the active restrictions sentence block for the description section.
 * If no restrictions exist, returns an explicit "none" instruction so the model
 * never guesses. If restrictions exist, only those lines are shown — the model
 * never sees the schema for fields that are absent.
 */
const buildRestrictionsBlock = ({ slowDaysS, timeRangeS, minSpendS, purchaseQtyS }) => {
  const days  = clean(slowDaysS);
  const time  = clean(timeRangeS);
  const spend = clean(minSpendS);
  const qty   = clean(purchaseQtyS);

  const lines = [];
  if (days)  lines.push(`Valid on ${days}.`);
  if (time)  lines.push(`Available between ${time}.`);
  if (spend) lines.push(`When you spend $${spend} or more.`);
  if (qty)   lines.push(`When you buy ${qty} or more.`);

  if (!lines.length) {
    return `ACTIVE CONDITIONS: None.
!! ZERO RESTRICTIONS EXIST FOR THIS COUPON !!
Do NOT write ANY of the following — not even once, not even subtly:
  - Any minimum spend or purchase amount (e.g. "spend $10", "minimum purchase", "when you spend")
  - Any purchase quantity (e.g. "buy 2", "purchase 2 or more")
  - Any day-of-week restriction (e.g. "Mondays only", "on weekends")
  - Any time window (e.g. "between 11 AM and 3 PM", "after 5 PM")
  - Any membership or exclusivity condition
The description must read as a fully unrestricted offer. Any invented restriction is a critical failure.`;
  }

  const exampleParts = [];
  if (days)  exampleParts.push(`on ${days}`);
  if (time)  exampleParts.push(`between ${time}`);
  if (spend) exampleParts.push(`when you spend $${spend} or more`);
  if (qty)   exampleParts.push(`when you buy ${qty} or more`);
  const exampleSentence = exampleParts.length > 1
    ? `(Example combined: "Valid ${exampleParts.join(' ')}.")`
    : '';

  return `ACTIVE CONDITIONS — combine smoothly into one natural sentence if multiple apply:
${lines.map(l => `  - ${l}`).join('\n')}
${exampleSentence}`.trim();
};

/**
 * Builds the Terms of Service conditions block.
 * Only sentences whose backing data is present are emitted — the model never
 * sees the template lines for absent fields.
 */
const buildToSConditions = ({
  itemNameS, addressS, minSpendS, purchaseQtyS,
  slowDaysS, timeRangeS, reservationHoursS,
  hasExclusivePremium, hasExclusiveFollowing, businessNameS,
}) => {
  const spend       = clean(minSpendS);
  const qty         = clean(purchaseQtyS);
  const days        = clean(slowDaysS);
  const time        = clean(timeRangeS);
  const reservation = clean(reservationHoursS);

  const lines = [];
  lines.push(`Valid for ${clean(itemNameS) || 'the specified item'} at ${clean(addressS) || 'the business location'} only.`);
  if (hasExclusivePremium)   lines.push('Exclusive to Premium Members.');
  if (hasExclusiveFollowing) lines.push(`Exclusive to followers of ${businessNameS}.`);
  if (spend)                 lines.push(`Min. spend $${spend} before tax.`);
  if (qty)                   lines.push(`Purchase of ${qty} items required.`);
  if (days || time)          lines.push(`Valid on ${[days, time].filter(Boolean).join(' ')}.`);
  if (reservation)           lines.push(`Must redeem within ${reservation} hours of reservation.`);
  return lines.join(' ');
};

/* -------------------- TITLE -------------------- */
/**
 * Title generation (MAX 50 chars)
 * Accepts old params { businessName, city, couponType, discountValue, tone }
 * Optional richer params:
 * { itemName, audience, expiryType, expiryDate, totalQuantity, desiredImpact, promotionName, slowDays, slowTimes, goal, scope, speed, rules, itemType }
 */
export const titlePrompt = (ctx = {}) => {
  const {
    businessName = 'Your Business',
    city = 'Your City',
    couponType = 'percentage',
    discountValue = '20',
    tone = 'friendly',

    // richer (optional)
    itemName = '',
    audience = [],
    expiryType = 'date',
    expiryDate = '',
    totalQuantity = '',
    desiredImpact = 'Steady',
    promotionName = '',
    slowDays = [],
    slowTimes = [],
    purchaseQty = null,

    // additional context (non-breaking)
    goal = '',
    scope = '',
    speed = '',
    rules = '',
    itemType = '',
  } = ctx;

  const sNorm = normalizeScope(scope || itemType);

  const offeringLabel =
    short(itemName) ||
    (sNorm === 'Individual Item'
      ? 'Selected Item'
      : sNorm === 'Product Line'
      ? 'Category'
      : sNorm === 'Storewide Promotion'
      ? 'Storewide'
      : 'Offer');

  const singleAudience =
    Array.isArray(audience) && audience.length === 1 ? audience[0] : '';

  const impactTone =
    desiredImpact === 'Max'
      ? 'Urgent, bold, high-excitement'
      : desiredImpact === 'Quick'
      ? 'Action-oriented, exciting'
      : 'Calm, value-focused, inviting';

  const urgentHint =
    expiryType === 'quantity' &&
    Number(totalQuantity) > 0 &&
    Number(totalQuantity) < 20
      ? 'Limited Available!'
      : expiryType === 'date' && isDateSoon(expiryDate)
      ? 'Ends Soon!'
      : desiredImpact === 'Max'
      ? "Don't Miss Out!"
      : desiredImpact === 'Quick'
      ? 'Save Now!'
      : '';

  const scopeRules =
    sNorm === 'Storewide Promotion'
      ? 'You MAY use the word "Storewide".'
      : 'You MUST NOT use the word "storewide" or imply storewide; focus on the selected item or category.';

  const slowDaysS   = Array.isArray(slowDays) && slowDays.length ? slowDays.join(', ') : '';
  const slowTimesS  = Array.isArray(slowTimes) && slowTimes.length ? slowTimes.join(', ') : '';
  const purchaseQtyS = purchaseQty != null && `${purchaseQty}`.trim() !== '' ? `${purchaseQty}` : '';

  const titleRestrictions = [];
  if (slowDaysS)    titleRestrictions.push(`Valid Days: ${slowDaysS}  → MUST prioritise DAY in title`);
  if (slowTimesS)   titleRestrictions.push(`Valid Times: ${slowTimesS}  → include TIME only if space allows`);
  if (purchaseQtyS) titleRestrictions.push(`Purchase Qty: ${purchaseQtyS}  → include ONLY if title still fits ≤36 chars`);

  const forbiddenBlock = buildForbiddenBlock({
    slowDaysS,
    timeRangeS: slowTimesS,
    minSpendS: '',
    purchaseQtyS,
    exclusivityContext: '',
  });

  return `
You are a skilled marketing copywriter for ${businessName} in ${city}.
Write ONLY a COUPON TITLE, MAX 36 CHARACTERS. No quotes, no trailing punctuation.

Context (optional): Goal=${short(goal) || '—'}, Scope=${short(sNorm) || '—'}, Speed=${short(speed) || '—'}.

${forbiddenBlock ? forbiddenBlock + '\n' : ''}
TITLE PRIORITY RULES (STRICT — DO NOT VIOLATE):

1) DAY restriction — only if provided above
2) TIME restriction — only if provided above and space allows
3) PURCHASE QUANTITY — only if provided above and fits ≤36 chars
4) ITEM name
5) DISCOUNT value

Rules:
- If slowDays is provided → MUST prioritize DAY in title
- If slowTimes is provided → include TIME only if space allows
- If purchaseQty is provided → include it ONLY if title still fits ≤36 chars
- NEVER truncate words
- If it doesn't fit, OMIT rather than force it
- If a restriction is not listed in the data above, OMIT it entirely

Data:
Item: ${offeringLabel}
Discount: ${fmtValueAndType(discountValue, couponType)}
Purchase Qty: ${purchaseQty ?? '—'}
Valid Days: ${list(slowDays)}
Valid Times: ${list(slowTimes)}

${titleRestrictions.length ? titleRestrictions.join('\n') : 'Restrictions: None.'}

Desired Offer Impact tone: ${impactTone}; General tone: ${tone}.
${scopeRules}
${rules ? `Additional constraints: ${rules}` : ''}

Return ONLY the final title text (≤36 chars).`.trim();
};

/* -------------------- DESCRIPTION -------------------- */
/**
 * Description generation (MAX 1000 chars, but most callers clamp to 200)
 * Old params: { businessName, city, goal, itemType, tone }
 * Richer optional: {
 *  itemName, itemDescription, couponType, discountValue, promotionName,
 *  expiryType, expiryDate, totalQuantity, minSpend, purchaseQty, audience,
 *  slowDays, slowTimes, desiredImpact, rules
 * }
 */
export const descriptionPrompt = (ctx = {}) => {
  const {
    businessName = 'Your Business',
    city = 'Your City',
    goal = '',
    itemType = 'storewide',
    tone = 'friendly',

    itemName = '',
    itemDescription = '',
    couponType = 'percentage',
    discountValue = '20',
    promotionName = '',
    expiryType = 'date',
    expiryDate = '',
    totalQuantity = '',
    minSpend = null,
    purchaseQty = null,
    audience = [],
    slowDays = [],
    slowTimes = [],
    desiredImpact = 'Steady',
    rules = '',
    currentDescription = '',
  } = ctx;

  const impactTone =
    desiredImpact === 'Max'
      ? 'Bold, urgent, high-value, direct, exciting'
      : desiredImpact === 'Quick'
      ? 'Enthusiastic, action-oriented, clear benefit'
      : 'Friendly, informative, value-oriented, inviting';

  const valueTypePhrase = fmtValueAndType(discountValue, couponType);

  const slowDaysS   = Array.isArray(slowDays) && slowDays.length ? slowDays.join(', ') : '';
  const timeRangeS  = Array.isArray(slowTimes) && slowTimes.length ? slowTimes.join(', ') : '';
  const minSpendS   = minSpend != null && `${minSpend}`.trim() !== '' ? `${minSpend}` : '';
  const purchaseQtyS = purchaseQty != null && `${purchaseQty}`.trim() !== '' ? `${purchaseQty}` : '';

  const restrictionsBlock = buildRestrictionsBlock({ slowDaysS, timeRangeS, minSpendS, purchaseQtyS });
  const forbiddenBlock = buildForbiddenBlock({ slowDaysS, timeRangeS, minSpendS, purchaseQtyS, exclusivityContext: '' });

  const mandatoryRestrictions = [];
  if (purchaseQtyS) mandatoryRestrictions.push(`- Purchase Quantity: clearly state "Buy ${purchaseQtyS}"`);
  if (slowDaysS)    mandatoryRestrictions.push(`- Valid Day(s): explicitly state "${slowDaysS}"`);
  if (timeRangeS)   mandatoryRestrictions.push(`- Valid Time(s): explicitly state "between ${timeRangeS}"`);
  if (minSpendS)    mandatoryRestrictions.push(`- Minimum Spend: explicitly state "Minimum spend $${minSpendS}"`);

  return `
You are a persuasive copywriter for ${businessName} in ${city}.
Write ONLY the COUPON DESCRIPTION, MAX 1000 CHARACTERS. No extra commentary.
${rules ? `Constraints: ${rules}` : ''}

${forbiddenBlock ? forbiddenBlock + '\n' : ''}
STRICT RULES:
- DO NOT mention the business goal or intent (e.g. "new customers", "off-peak", "boost sales").
- DO NOT say "Perfect for ..." or anything implying a marketing goal.
- Focus only on what the customer gets and any redemption requirements.

MANDATORY REQUIREMENTS (DO NOT SKIP):
- ALWAYS mention the ITEM.
- ALWAYS mention the DISCOUNT.

${mandatoryRestrictions.length
  ? `IF PROVIDED, YOU MUST ALWAYS INCLUDE:\n${mandatoryRestrictions.join('\n')}`
  : ''}

Examples:
- "Buy 2 Mango Drinks and save 10% this Monday."
- "Available Monday between 8–12 PM."
- "Redeem this deal by purchasing 2 items."
${restrictionsBlock}

Data:
Item: ${short(itemName) || 'Storewide'} — ${short(itemDescription)}
Discount: ${valueTypePhrase}
Purchase Quantity: ${purchaseQty ?? '—'}
Valid Days: ${list(slowDays)}
Valid Times: ${list(slowTimes)}
Minimum Spend: ${minSpend ?? '—'}

Tone: ${impactTone} (overall tone: ${tone}).

Return ONLY the description text (≤1000 chars).`.trim();
};

/* -------------------- TERMS -------------------- */
/**
 * Terms & Conditions — SINGLE PARAGRAPH
 * Old call: termsPrompt({ businessName })
 * Richer optional: {
 *  location, itemName, couponType, discountValue,
 *  targetSegment, expiryType, expiryDate, totalQuantity,
 *  perUserLimitType, perUserLimitCount, redemptionType,
 *  minSpend, purchaseQty, desiredDays, timeRange,
 *  reservationHours, redeemableWithPoints
 * }
 */
export const termsPrompt = (ctx = {}) => {
  const {
    businessName = 'Your Business',
    location = 'the business location',
    itemName = 'the specified item or storewide offer',
    couponType = 'Percentage Off',
    discountValue = '20%',

    targetSegment = '',
    expiryType = 'date',
    expiryDate = '',
    totalQuantity = '',

    perUserLimitType = 'unlimited',
    perUserLimitCount = '',

    redemptionType = 'manual',
    minSpend = null,
    purchaseQty = null,
    desiredDays = [],
    timeRange = '',
    reservationHours = '',
    redeemableWithPoints = false,
  } = ctx;

  const mustIncludeMinSpend    = minSpend != null && `${minSpend}`.trim() !== '';
  const mustIncludePurchaseQty = purchaseQty != null && `${purchaseQty}`.trim() !== '';
  const desiredDaysS  = Array.isArray(desiredDays) && desiredDays.length ? desiredDays.join(', ') : '';
  const timeRangeS    = short(timeRange);
  const reservationS  = short(reservationHours);

  const valueTypePhrase = fmtValueAndType(discountValue, couponType);

  const conditionLines = [];
  if (mustIncludeMinSpend)    conditionLines.push(`  • INCLUDE EXACT SENTENCE: "A minimum purchase of $${minSpend} (before taxes and fees) is required."`);
  if (mustIncludePurchaseQty) conditionLines.push(`  • INCLUDE EXACT SENTENCE: "A minimum purchase quantity of ${purchaseQty} applies."`);
  if (desiredDaysS)           conditionLines.push(`  • Valid days: ${desiredDaysS}`);
  if (timeRangeS)             conditionLines.push(`  • Time window: ${timeRangeS}`);
  if (reservationS)           conditionLines.push(`  • Reservation window: ${reservationS}`);
  conditionLines.push(`  • Points allowed: ${redeemableWithPoints ? 'Yes' : 'No'}`);

  const tosForbidden = [];
  if (!mustIncludeMinSpend)    tosForbidden.push('- Minimum spend requirements');
  if (!mustIncludePurchaseQty) tosForbidden.push('- Purchase quantity requirements');
  if (!desiredDaysS)           tosForbidden.push('- Day-of-week restrictions');
  if (!timeRangeS)             tosForbidden.push('- Time window or hours restrictions');
  if (!reservationS)           tosForbidden.push('- Reservation or booking windows');

  const tosForbiddenBlock = tosForbidden.length
    ? `\nFORBIDDEN — do NOT mention these absent fields anywhere:\n${tosForbidden.join('\n')}\n`
    : '';

  return `
Write ONE concise paragraph of Terms & Conditions for a coupon. No headings, no bullets.

You MUST produce a single paragraph that includes ALL applicable items below. If a field is provided (not blank), you MUST include the exact sentence shown.
This entire paragraph MUST be under 580 characters.
${tosForbiddenBlock}

Always include:
- "This coupon is valid for ${itemName} and provides ${valueTypePhrase}."
- "It is valid exclusively at ${location}."

Target segment (ONLY if provided/restrictive): ${short(targetSegment) || 'None'}
${targetSegment ? `- This coupon is only valid for ${targetSegment}.` : ''}

Expiry (choose one):
  • If date → "This offer expires on ${expiryDate}."
  • If quantity → "Redemption is limited to a total of ${totalQuantity} uses."

Per-user limits (choose one):
  • One Time → "Strictly limited to one redemption per customer."
  • Multiple → "Multiple redemptions permitted per customer${perUserLimitCount ? ` (up to ${perUserLimitCount})` : ''}."
  • Unlimited → "Unlimited redemptions per customer (subject to the business's overall monthly redemption capacity)."

Redemption method (choose one):
  • Auto → "This coupon will be automatically applied within the app and is redeemable in-store only."
  • Manual → "This coupon must be presented within the app for manual redemption by staff and is redeemable in-store only."

Conditions (ONLY if listed below — skip entirely if the list is empty):
${conditionLines.join('\n')}

ALWAYS append these standard clauses, integrated smoothly:
"This coupon cannot be combined with any other offer or promotion. The business reserves the right to modify or terminate this offer at any time without prior notice. This coupon holds no cash value and is not transferable or redeemable for cash. This offer is non-refundable. Additional terms and conditions may apply; please inquire in-store for full details."

Return ONLY the final paragraph.`.trim();
};

/* -------------------- ALL-IN-ONE -------------------- */
/**
 * Generates ONE prompt that returns:
 *  - Title (<= 36 chars)
 *  - Description (<= 1000 chars)
 *  - Terms of Service (<= 580 chars, single paragraph)
 *
 * Output must be labeled exactly:
 *   Title: ...
 *   Description: ...
 *   Terms of Service: ...
 */
export const allCouponPrompt = (ctx = {}) => {
  const {
    // Business
    businessName = 'Your Business',
    city = 'Your City',
    address = '',
    location = '',

    // Item
    itemName = '',
    itemDescription = '',
    itemPrice = null,

    // Discount
    couponType = 'percentage',
    discountValue = '20',

    // Conditions
    purchaseQty = null,
    minSpend = null,
    slowDays = [],
    timeRange = '',
    reservationHours = '',

    // Expiry limits
    totalQuantity = '',
    expiryDate = '',

    // Exclusivity
    exclusiveFollowing = null,
    exclusivePremium = null,

    // Strategy
    targetSegment = '',
    frequencyTarget = '',
    speed = 'Steady',

    // Title variation controls
    titleAngle = '',
    titleAngleKey = '',
    variantIndex = '',
    avoidanceContext = '',

    // Extra
    rules = '',
  } = ctx;

  const s = val => (val == null ? '' : String(val).trim());

  const businessNameS   = s(businessName) || 'Your Business';
  const cityS           = s(city) || 'Your City';
  const addressS        = s(address);
  const locationS       = s(location) || addressS || 'the business location';
  const itemNameS       = s(itemName);
  const itemDescS       = s(itemDescription);

  // Discount display
  const discountValueS  = s(discountValue);
  const couponTypeS     = s(couponType);
  const discountDisplay = fmtValueAndType(discountValueS, couponTypeS);

  // Regular price / final price (only if item price is provided and discount is meaningful)
  let priceBlock = '';
  if (itemPrice != null && `${itemPrice}`.trim() !== '' && couponTypeS !== 'points') {
    const price    = parseFloat(itemPrice);
    const discount = parseFloat(discountValueS);
    if (!Number.isNaN(price) && !Number.isNaN(discount) && price > 0) {
      const showIt =
        (couponTypeS === 'percentage' && discount > 10) ||
        (couponTypeS === 'dollar' && (discount / price) > 0.1);
      if (showIt) {
        const finalPrice =
          couponTypeS === 'percentage'
            ? (price * (1 - discount / 100)).toFixed(2)
            : (price - discount).toFixed(2);
        priceBlock = `Regular price: $${price.toFixed(2)}. State the final price as $${finalPrice}.`;
      }
    }
  }

  // Restrictions — resolve to empty string when absent
  const purchaseQtyS   = purchaseQty != null && `${purchaseQty}`.trim() !== '' ? s(purchaseQty) : '';
  const minSpendS      = minSpend != null && `${minSpend}`.trim() !== '' ? s(minSpend) : '';
  const slowDaysS      = Array.isArray(slowDays) && slowDays.length ? slowDays.join(', ') : '';
  const timeRangeS     = s(timeRange);
  const reservationS   = s(reservationHours);

  // Exclusivity
  const hasExclusivePremium   = exclusivePremium === true;
  const hasExclusiveFollowing = exclusiveFollowing === true;

  const targetSegmentS   = s(targetSegment) || '';
  const frequencyTargetS = s(frequencyTarget) || '';
  const speedS           = s(speed) || 'Steady';

  let exclusivityContext = '';
  if (hasExclusivePremium) {
    exclusivityContext = 'Reserved exclusively for My Couponbook Premium members.';
  } else if (hasExclusiveFollowing) {
    exclusivityContext = `Valid only for users who follow ${businessNameS} in the app.`;
  }

  let frequencyContext = '';
  if (frequencyTargetS) {
    const freq = frequencyTargetS.toLowerCase();
    if (freq.includes('multiple') || freq.includes('week')) {
      frequencyContext = 'Perfect for your weekly routine.';
    } else if (freq.includes('month')) {
      frequencyContext = 'Your monthly treat awaits.';
    } else if (freq.includes('season')) {
      frequencyContext = 'Make it a seasonal tradition.';
    } else if (freq.includes('once')) {
      frequencyContext = 'A special one-time opportunity.';
    }
  }

  // Title single-day hint
  const singleDayS = Array.isArray(slowDays) && slowDays.length === 1 ? slowDays[0] : '';

  // --- Dynamic blocks ---
  const forbiddenBlock     = buildForbiddenBlock({ slowDaysS, timeRangeS, minSpendS, purchaseQtyS, exclusivityContext });
  const restrictionsBlock  = buildRestrictionsBlock({ slowDaysS, timeRangeS, minSpendS, purchaseQtyS });
  const tosConditionsBlock = buildToSConditions({
    itemNameS, addressS, minSpendS, purchaseQtyS,
    slowDaysS, timeRangeS, reservationHoursS: reservationS,
    hasExclusivePremium, hasExclusiveFollowing, businessNameS,
  });

  // Title guardrail: only show restriction fields that are actually present
  const titleRestrictionHints = [];
  if (singleDayS) {
    titleRestrictionHints.push(`Exactly one valid day is set (${singleDayS}) — MUST appear in the title.`);
  } else if (slowDaysS) {
    titleRestrictionHints.push(`Valid days: ${slowDaysS} — include in the title if it fits ≤36 chars.`);
  }
  if (timeRangeS)   titleRestrictionHints.push(`Valid time range: ${timeRangeS} — include only if title fits ≤36 chars.`);
  if (purchaseQtyS) titleRestrictionHints.push(`Purchase qty: ${purchaseQtyS} — include only if title still fits ≤36 chars.`);

  return `
SYSTEM CONTEXT & ROLE
=====================
You are an elite Behavioural Copywriter for ${businessNameS} in ${cityS}${addressS ? ` at ${addressS}` : ''}.
Your objective is to write a high-converting, 3-part coupon that drives immediate in-store foot traffic.
Voice: Authentic, local, approachable, and appetising.

UNIVERSAL DATA PROTOCOLS
========================
1. SPELLING: Use Canadian/British English exclusively (e.g. favourite, savour, centre).
2. FORMATTING: No em dashes. Use natural symbols for currency and percentages (e.g. "$5" or "20%"). Never write "5 Dollar" or "20 Percent".
3. TONE: Do NOT expose any marketing goal, target segment, or business strategy to the reader. Shape only vocabulary and emotional appeal internally.

${forbiddenBlock ? forbiddenBlock + '\n' : ''}
=========================================
SECTION 1: TITLE
Constraint: MAX 36 CHARACTERS. Single line. No trailing punctuation.

This is Title ${variantIndex || '—'} of 3.

TITLE ANGLE (PRIMARY DIRECTIVE - MUST FOLLOW):
${titleAngle ? titleAngle : 'Create a compelling, benefit-focused title'}

CRITICAL VARIETY RULES (ABSOLUTE REQUIREMENTS):
1. Your title MUST be distinctly different from the other ${variantIndex === '1' ? 'upcoming' : 'previous'} titles in this set
2. Do NOT start with the same first word as any other title
3. FORBIDDEN generic starters: Deal, Offer, Special, Save, Discount, Get, Exclusive, Limited
${avoidanceContext ? `4. ADDITIONALLY FORBIDDEN: ${avoidanceContext}` : ''}
5. If you cannot follow the title angle within 36 chars, prioritize the character limit and variety rules

Constraints: 2 to 5 words. Max 36 characters. Option ${variantIndex || '1'} of 3.

Write a catchy, scroll-stopping title.
- The item (${itemNameS || 'the offer'}) MUST be the main focal point.
- The savings (${discountDisplay}) MUST be the secondary focal point.
- Drop filler action verbs if needed to prioritise item and value (e.g. "$5 Off Signature Steaks").
- Do not sound corporate or overly salesy. No quotes.
${avoidanceContext ? `- Avoid: "${avoidanceContext}".` : ''}
${titleRestrictionHints.length ? titleRestrictionHints.map(h => `- ${h}`).join('\n') : ''}

=========================================
SECTION 2: DESCRIPTION
Constraint: 300 to 800 characters. Exactly 1 paragraph.

Write a persuasive, fluid description using this 4-step architecture:

1. HOOK: Vividly describe the experience and taste/benefit of ${itemNameS || 'the offer'}${itemDescS ? ` (${itemDescS})` : ''}.

2. VALUE: Introduce the ${discountDisplay} naturally.
${priceBlock
  ? `   ${priceBlock}`
  : '   Do NOT invent a regular price or final price — focus on the discount value alone.'}

3. RESTRICTIONS:
${exclusivityContext ? `   - Inject this exact sentence: "${exclusivityContext}"` : ''}
   ${restrictionsBlock}

4. LOCAL CTA: Conclude with a warm nod to ${businessNameS}'s place in the ${cityS} community and a confident call-to-action.

=========================================
SECTION 3: TERMS OF SERVICE
Constraint: 1 paragraph. Legal validity.

Include ONLY these applicable sentences:
${tosConditionsBlock}
Present in-app for redemption. Limit 1 per user per visit. Excludes tax, gratuity, and alcohol (where applicable). Valid for in-store redemption only; not valid on third-party delivery apps. Cannot be used to purchase gift cards or applied to prior purchases. Cannot be combined with other offers. All sales are final. No cash value.

=========================================
PRE-OUTPUT SELF-CHECK (complete this silently before writing your output)
Read back your drafted description and terms. Answer each question:
  1. Did I mention a minimum spend or dollar threshold? → If YES, remove it.
  2. Did I mention a purchase quantity? → If YES, remove it.
  3. Did I mention a day of the week? → If YES, remove it (unless listed in ACTIVE CONDITIONS above).
  4. Did I mention a time window or hours? → If YES, remove it (unless listed in ACTIVE CONDITIONS above).
  5. Did I mention membership, premium, or follower requirements? → If YES, remove it (unless exclusivity was set above).
Only proceed to output after all five answers are NO.

=========================================
OUTPUT GENERATION
Verify your math and exclusivity strings. Output exactly using these three labels:
Title:
Description:
Terms of Service:
`.trim();
};

/* -------------------- IMAGE (HERO) -------------------- */
/**
 * Image suggestion / generation
 * Old call: logoImagePrompt({ businessName, vibes })
 * Richer optional: { itemName, categoryMain, categorySub, visualReferenceProvided, desiredImpact }
 * Returns a concise prompt for your AI image route (500x500 focus).
 */
export const logoImagePrompt = (ctx = {}) => {
  const {
    businessName = 'Your Business',
    vibes = 'simple, modern, flat, high-contrast',

    // richer (optional)
    itemName = '',
    categoryMain = '',
    categorySub = '',
    visualReferenceProvided = false,
    desiredImpact = 'Steady',
  } = ctx;

  const mood =
    desiredImpact === 'Max'
      ? 'bold, high-energy, eye-catching, dynamic lighting'
      : desiredImpact === 'Quick'
      ? 'appealing, lively, clear focal point, modern'
      : 'calm, authentic, high-quality, inviting';

  return `
Create a compelling 500x500 hero image for a coupon (no text in the image).
Focus on the experience/benefit rather than a flat logo.

Context:
- Business: ${businessName}
- Offering: ${short(itemName) || 'Storewide promotion'}
- Category: ${short(categoryMain)} / ${short(categorySub)}
- Visual reference provided by user: ${visualReferenceProvided ? 'Yes' : 'No'}
- Desired mood: ${mood}
- Style hints: ${vibes}

Return ONLY a concise scene description suitable for an AI image generator.`.trim();
};

// // Central place to tune prompts without touching backend
// // Backward-compatible with your previous callers, but supports richer context.

// /* -------------------- helpers -------------------- */
// const short = v => (v ?? '').toString().trim();
// const list = arr => (Array.isArray(arr) && arr.length ? arr.join(', ') : '—');
// const isDateSoon = iso => {
//   if (!iso) return false;
//   const d = new Date(iso);
//   if (isNaN(d)) return false;
//   const diffDays = (d - new Date()) / (1000 * 60 * 60 * 24);
//   return diffDays <= 7;
// };

// const normalizeScope = s => {
//   const val = (s || '').toString().toLowerCase();
//   if (!val) return '';
//   if (val.includes('individual')) return 'Individual Item';
//   if (val.includes('line') || val.includes('category')) return 'Product Line';
//   if (val.includes('store') || val.includes('storewide') || val === 'all')
//     return 'Storewide Promotion';
//   return '';
// };

// /* Optional: very light formatting if callers pass raw types */
// const fmtValueAndType = (discountValue, couponType) => {
//   const val = short(discountValue);
//   const type = short(couponType).toLowerCase();
//   if (!val || !type) return `${val} ${couponType}`.trim();
//   if (type.includes('percent')) return `${val}% off`;
//   if (type.includes('dollar') || type.includes('amount')) return `$${val} off`;
//   if (type.includes('points')) return `${val} points`; // "points back" semantics can be added in the sentence
//   if (type.includes('bogo')) return `BOGO (${val})`;
//   if (type.includes('free')) return `FREE (${val})`;
//   return `${val} ${couponType}`;
// };

// /* -------------------- TITLE -------------------- */
// /**
//  * Title generation (MAX 36 chars)
//  * Accepts old params { businessName, city, couponType, discountValue, tone }
//  * Optional richer params:
//  * { itemName, audience, expiryType, expiryDate, totalQuantity, desiredImpact, promotionName, slowDays, slowTimes, goal, scope, speed, rules, itemType }
//  */
// export const titlePrompt = (ctx = {}) => {
//   const {
//     businessName = 'Your Business',
//     city = 'Your City',
//     couponType = 'percentage', // 'percentage' | 'dollar' | 'points' | 'BOGO' | 'FREE'
//     discountValue = '20',
//     tone = 'friendly',

//     // richer (optional)
//     itemName = '',
//     audience = [], // array of audience labels
//     expiryType = 'date', // 'date' | 'quantity'
//     expiryDate = '',
//     totalQuantity = '',
//     desiredImpact = 'Steady', // 'Steady' | 'Quick' | 'Max'
//     promotionName = '',
//     slowDays = [],
//     slowTimes = [],
//     purchaseQty = null,

//     // additional context (non-breaking)
//     goal = '',
//     scope = '', // expected values like "Individual Item" | "Product Line" | "Storewide Promotion"
//     speed = '',
//     rules = '',
//     itemType = '', // legacy alias for scope
//   } = ctx;

//   const sNorm = normalizeScope(scope || itemType);

//   // Offering label respects scope to avoid accidental "Storewide" when user chose Individual Item
//   // Priority: explicit itemName → scope-aware fallback
//   const offeringLabel =
//     short(itemName) ||
//     (sNorm === 'Individual Item'
//       ? 'Selected Item'
//       : sNorm === 'Product Line'
//       ? 'Category'
//       : sNorm === 'Storewide Promotion'
//       ? 'Storewide'
//       : 'Offer');

//   const singleAudience =
//     Array.isArray(audience) && audience.length === 1 ? audience[0] : '';

//   const impactTone =
//     desiredImpact === 'Max'
//       ? 'Urgent, bold, high-excitement'
//       : desiredImpact === 'Quick'
//       ? 'Action-oriented, exciting'
//       : 'Calm, value-focused, inviting';

//   const urgentHint =
//     expiryType === 'quantity' &&
//     Number(totalQuantity) > 0 &&
//     Number(totalQuantity) < 20
//       ? 'Limited Available!'
//       : expiryType === 'date' && isDateSoon(expiryDate)
//       ? 'Ends Soon!'
//       : desiredImpact === 'Max'
//       ? "Don't Miss Out!"
//       : desiredImpact === 'Quick'
//       ? 'Save Now!'
//       : '';

//   // Hard guardrails to keep scope accurate in the title copy
//   const scopeRules =
//     sNorm === 'Storewide Promotion'
//       ? 'You MAY use the word "Storewide".'
//       : 'You MUST NOT use the word "storewide" or imply storewide; focus on the selected item or category.';

//   return `
// You are a skilled marketing copywriter for ${businessName} in ${city}.
// Write ONLY a COUPON TITLE, MAX 36 CHARACTERS. No quotes, no trailing punctuation.

// Context (optional): Goal=${short(goal) || '—'}, Scope=${short(sNorm) ||
//     '—'}, Speed=${short(speed) || '—'}.

// TITLE PRIORITY RULES (STRICT — DO NOT VIOLATE):

// 1) DAY restriction (if provided)
//    - Example: "Monday Deal", "Weekend Special"

// 2) TIME restriction (if space allows)
//    - Example: "8–12 PM", "Morning Deal"

// 3) PURCHASE QUANTITY (ONLY if it fits naturally)
//    - Example: "Buy 2", "Buy Two"

// 4) ITEM name

// 5) DISCOUNT value

// Rules:
// - If slowDays is provided → MUST prioritize DAY in title
// - If slowTimes is provided → include TIME only if space allows
// - If purchaseQty is provided → include it ONLY if title still fits ≤36 chars
// - NEVER truncate words
// - If it doesn’t fit, OMIT quantity (do NOT force it)

// Data:
// Item: ${offeringLabel}
// Discount: ${fmtValueAndType(discountValue, couponType)}
// Purchase Qty: ${purchaseQty ?? '—'}
// Valid Days: ${list(slowDays)}
// Valid Times: ${list(slowTimes)}


// Desired Offer Impact tone: ${impactTone}; General tone: ${tone}.
// ${scopeRules}
// ${rules ? `Additional constraints: ${rules}` : ''}

// Return ONLY the final title text (≤36 chars).`.trim();
// };

// /* -------------------- DESCRIPTION -------------------- */
// /**
//  * Description generation (MAX 1000 chars, but most callers clamp to 200)
//  * Old params: { businessName, city, goal, itemType, tone }
//  * Richer optional: {
//  *  itemName, itemDescription, couponType, discountValue, promotionName,
//  *  expiryType, expiryDate, totalQuantity, minSpend, purchaseQty, audience,
//  *  slowDays, slowTimes, desiredImpact, rules
//  * }
//  */
// export const descriptionPrompt = (ctx = {}) => {
//   const {
//     businessName = 'Your Business',
//     city = 'Your City',
//     goal = '', // keep in ctx for internal guidance if you want, but DO NOT expose it to the model
//     itemType = 'storewide',
//     tone = 'friendly',

//     itemName = '',
//     itemDescription = '',
//     couponType = 'percentage',
//     discountValue = '20',
//     promotionName = '',
//     expiryType = 'date',
//     expiryDate = '',
//     totalQuantity = '',
//     minSpend = null,
//     purchaseQty = null,
//     audience = [],
//     slowDays = [],
//     slowTimes = [],
//     desiredImpact = 'Steady',
//     rules = '',
//     currentDescription = '',
//   } = ctx;

//   const impactTone =
//     desiredImpact === 'Max'
//       ? 'Bold, urgent, high-value, direct, exciting'
//       : desiredImpact === 'Quick'
//       ? 'Enthusiastic, action-oriented, clear benefit'
//       : 'Friendly, informative, value-oriented, inviting';

//   const valueTypePhrase = fmtValueAndType(discountValue, couponType);

//   return `
// You are a persuasive copywriter for ${businessName} in ${city}.
// Write ONLY the COUPON DESCRIPTION, MAX 1000 CHARACTERS. No extra commentary.
// ${rules ? `Constraints: ${rules}` : ''}

// STRICT RULES:
// - DO NOT mention the business goal or intent (examples: "new customers", "repeat customers", "off-peak", "slow periods", "boost sales", "perfect for...").
// - DO NOT say "Perfect for ..." or anything that implies a marketing goal.
// - Focus only on what the customer gets and the redemption requirements.

// MANDATORY REQUIREMENTS (DO NOT SKIP):
// - ALWAYS mention the ITEM.
// - ALWAYS mention the DISCOUNT.

// IF PROVIDED, YOU MUST ALWAYS INCLUDE:
// - Purchase Quantity → clearly stated (e.g. "Buy 2")
// - Valid Day(s) → explicitly stated (e.g. "This Monday")
// - Valid Time(s) → explicitly stated (e.g. "between 8–12 PM")
// - Minimum Spend → explicitly stated (e.g. "Minimum spend $25")

// These restrictions MUST be consistent and explicit.
// Do NOT imply — state them clearly in sentence form.

// Examples:
// - "Buy 2 Mango Drinks and save 10% this Monday."
// - "Available Monday between 8–12 PM."
// - "Redeem this deal by purchasing 2 items."

// Data:
// Item: ${short(itemName) || 'Storewide'} — ${short(itemDescription)}
// Discount: ${valueTypePhrase}
// Purchase Quantity: ${purchaseQty ?? '—'}
// Valid Days: ${list(slowDays)}
// Valid Times: ${list(slowTimes)}
// Minimum Spend: ${minSpend ?? '—'}

// Tone: ${impactTone} (overall tone: ${tone}).

// Return ONLY the description text (≤1000 chars).`.trim();
// };

// /* -------------------- TERMS -------------------- */
// /**
//  * Terms & Conditions — SINGLE PARAGRAPH
//  * Old call: termsPrompt({ businessName })
//  * Richer optional: {
//  *  location, itemName, couponType, discountValue,
//  *  targetSegment, expiryType, expiryDate, totalQuantity,
//  *  perUserLimitType, perUserLimitCount, redemptionType,
//  *  minSpend, purchaseQty, desiredDays, timeRange,
//  *  reservationHours, redeemableWithPoints
//  * }
//  */
// export const termsPrompt = (ctx = {}) => {
//   const {
//     businessName = 'Your Business',
//     location = 'the business location',
//     itemName = 'the specified item or storewide offer',
//     couponType = 'Percentage Off',
//     discountValue = '20%',

//     targetSegment = '', // e.g., "Students", "Premium Members"
//     expiryType = 'date', // 'date' | 'quantity'
//     expiryDate = '',
//     totalQuantity = '',

//     perUserLimitType = 'unlimited', // 'unlimited' | 'oneTime' | 'multiple'
//     perUserLimitCount = '',

//     redemptionType = 'manual', // 'manual' | 'auto'
//     minSpend = null,
//     purchaseQty = null,
//     desiredDays = [],
//     timeRange = '',
//     reservationHours = '',
//     redeemableWithPoints = false,
//   } = ctx;

//   // Build explicit inclusion lines so the model *must* place them if provided
//   const mustIncludeMinSpend = minSpend != null && minSpend !== '';
//   const mustIncludePurchaseQty = purchaseQty != null && purchaseQty !== '';

//   const valueTypePhrase = fmtValueAndType(discountValue, couponType);

//   return `
// Write ONE concise paragraph of Terms & Conditions for a coupon. No headings, no bullets.

// "This entire paragraph MUST be under 580 characters.",

// You MUST produce a single paragraph that includes ALL applicable items below. If a field is provided (not blank), you MUST include the exact sentence shown.

// Always include:
// - "This coupon is valid for ${itemName} and provides ${valueTypePhrase}."
// - "It is valid exclusively at ${location}."

// Target segment (ONLY if provided/restrictive): ${short(targetSegment) || '—'}
// ${targetSegment ? `This coupon is only valid for ${targetSegment}.` : ''}

// Expiry (choose one):
//   • If date → "This offer expires on ${expiryDate}."
//   • If quantity → "Redemption is limited to a total of ${totalQuantity} uses."

// Per-user limits (choose one):
//   • One Time → "Strictly limited to one redemption per customer."
//   • Multiple → "Multiple redemptions permitted per customer${
//     perUserLimitCount ? ` (up to ${perUserLimitCount})` : ''
//   }."
//   • Unlimited → "Unlimited redemptions per customer (subject to the business's overall monthly redemption capacity)."

// Redemption method (choose one):
//   • Auto → "This coupon will be automatically applied within the app and is redeemable in-store only."
//   • Manual → "This coupon must be presented within the app for manual redemption by staff and is redeemable in-store only."

// Conditions (ONLY if provided):
// ${
//   mustIncludeMinSpend
//     ? `  • INCLUDE EXACT SENTENCE: "A minimum purchase of $${minSpend} (before taxes and fees) is required."`
//     : ''
// }
// ${
//   mustIncludePurchaseQty
//     ? `  • INCLUDE EXACT SENTENCE: "A minimum purchase quantity of ${purchaseQty} applies."`
//     : ''
// }
//   • Valid days: ${list(desiredDays)}
//   • Time window: ${short(timeRange) || '—'}
//   • Reservation window: ${short(reservationHours) || '—'}
//   • Points allowed: ${redeemableWithPoints ? 'Yes' : 'No'}

// ALWAYS append these standard clauses, integrated smoothly:
// "This coupon cannot be combined with any other offer or promotion. The business reserves the right to modify or terminate this offer at any time without prior notice. This coupon holds no cash value and is not transferable or redeemable for cash. This offer is non-refundable. Additional terms and conditions may apply; please inquire in-store for full details."

// Return ONLY the final paragraph.`.trim();
// };

// /**
//  * Generates ONE prompt that returns:
//  *  - Title (<= 36 chars)
//  *  - Description (<= 1000 chars)
//  *  - Terms of Service (<= 580 chars, single paragraph)
//  *
//  * Output must be labeled exactly:
//  *   Title: ...
//  *   Description: ...
//  *   Terms of Service: ...
//  *
//  * Uses the same ctx fields as titlePrompt/descriptionPrompt/termsPrompt (backward compatible).
//  */
// /**
//  * Generates ONE prompt that returns:
//  *  - Title (<= 36 chars)
//  *  - Description (<= 1000 chars)
//  *  - Terms of Service (<= 580 chars, single paragraph)
//  *
//  * Output must be labeled exactly:
//  *   Title: ...
//  *   Description: ...
//  *   Terms of Service: ...
//  */
// export const allCouponPrompt = (ctx = {}) => {
//   const {
//     // Business
//     businessName = 'Your Business',
//     city = 'Your City',
//     address = '',
//     location = '',

//     // Item
//     itemName = '',
//     itemDescription = '',
//     itemPrice = null,

//     // Discount
//     couponType = 'percentage',
//     discountValue = '20',

//     // Conditions
//     purchaseQty = null,
//     minSpend = null,
//     slowDays = [],
//     timeRange = '',
//     reservationHours = '',

//     // Expiry limits
//     totalQuantity = '',
//     expiryDate = '',

//     // Exclusivity
//     exclusiveFollowing = null,
//     exclusivePremium = null,

//     // Strategy
//     targetSegment = '',
//     frequencyTarget = '',
//     speed = 'Steady',

//     // Title variation controls
//     titleAngle = '',
//     titleAngleKey = '',
//     variantIndex = '',
//     avoidanceContext  = '',

//     // Extra
//     rules = '',
//   } = ctx;

//   const s = val => (val == null ? '' : String(val).trim());

//   const businessNameS = s(businessName) || 'Your Business';
//   const cityS = s(city) || 'Your City';
//   const addressS = s(address) || '';
//   const locationS = s(location) || (s(address) ? s(address) : 'the business location');

//   const itemNameS = s(itemName) || '';
//   const itemDescS = s(itemDescription) || '';

//   // Calculate if we should show regular price (>10% off, not points)
//   let showRegularPrice = false;
//   let regularPriceS = '';
//   if (itemPrice != null && `${itemPrice}`.trim() !== '' && couponType !== 'points') {
//     const price = parseFloat(itemPrice);
//     const discount = parseFloat(discountValue);
//     if (!Number.isNaN(price) && !Number.isNaN(discount) && price > 0) {
//       if (couponType === 'percentage' && discount > 10) {
//         showRegularPrice = true;
//         regularPriceS = `$${price.toFixed(2)}`;
//       } else if (couponType === 'dollar' && (discount / price) > 0.1) {
//         showRegularPrice = true;
//         regularPriceS = `$${price.toFixed(2)}`;
//       }
//     }
//   }

//   const discountValueS = s(discountValue) || '';
//   const couponTypeS = s(couponType) || '';

//   const purchaseQtyS = purchaseQty != null && `${purchaseQty}`.trim() !== '' ? s(purchaseQty) : '';
//   const minSpendS = minSpend != null && `${minSpend}`.trim() !== '' ? s(minSpend) : '';

//   const slowDaysS = Array.isArray(slowDays) && slowDays.length ? slowDays.join(', ') : '';
//   const timeRangeS = s(timeRange) || '';
//   const reservationHoursS = s(reservationHours) || '';

//   const totalQuantityS = s(totalQuantity) || '';
//   const expiryDateS = s(expiryDate) || '';

//   const hasExclusiveFollowing = exclusiveFollowing === true;
//   const hasExclusivePremium = exclusivePremium === true;

//   const targetSegmentS = s(targetSegment) || '';
//   const frequencyTargetS = s(frequencyTarget) || '';
//   const speedS = s(speed) || 'Steady';

//   let exclusivityContext = '';
//   if (hasExclusivePremium) {
//     exclusivityContext = 'This is an exclusive offer for Premium Members only.';
//   } else if (hasExclusiveFollowing) {
//     exclusivityContext = `This is an exclusive offer for followers of ${businessNameS}.`;
//   }
  

//   let frequencyContext = '';
//   if (frequencyTargetS) {
//     const freq = frequencyTargetS.toLowerCase();
//     if (freq.includes('multiple') || freq.includes('week')) {
//       frequencyContext = 'Perfect for your weekly routine.';
//     } else if (freq.includes('month')) {
//       frequencyContext = 'Your monthly treat awaits.';
//     } else if (freq.includes('season')) {
//       frequencyContext = 'Make it a seasonal tradition.';
//     } else if (freq.includes('once')) {
//       frequencyContext = 'A special one-time opportunity.';
//     }
//   }

//   // Determine if slowDays is a single day for title logic
//   const singleDayS = Array.isArray(slowDays) && slowDays.length === 1 ? slowDays[0] : '';

//   return `
// ROLE:
// You are a Senior Copywriter for ${businessNameS} in ${cityS}${addressS ? ` at ${addressS}` : ''}, specializing in high-conversion local promotions.
// Your goal is to craft compelling, modern coupon copy designed to drive immediate in-store action.
// Your writing is exciting, efficient, and results-oriented—mastering the art of "less is more" to turn passive scrollers into paying customers.

// INPUT DATA (Completely ignore if null/empty):
// Item Details: ${itemNameS}${itemDescS ? ` — ${itemDescS}` : ''}${showRegularPrice ? ` (Regular Price: ${regularPriceS})` : ''}

// Discount: ${discountValueS} ${couponTypeS}

// Conditions (Completely ignore if null/empty):
// ${purchaseQtyS ? `- Min Purchase Qty: ${purchaseQtyS}` : ''}
// ${minSpendS ? `- Min Spend: $${minSpendS}` : ''}
// ${slowDaysS ? `- Valid Days: ${slowDaysS}` : ''}
// ${timeRangeS ? `- Time Range: ${timeRangeS}` : ''}
// ${reservationHoursS ? `- Reservation Time: ${reservationHoursS} hours` : ''}

// Expiry Limits (Completely ignore if null/empty):
// ${totalQuantityS ? `- Total Coupons Available: ${totalQuantityS}` : ''}
// ${expiryDateS ? `- Expiry Date: ${expiryDateS}` : ''}

// Exclusivity (Completely ignore if null/empty):
// ${hasExclusivePremium ? '- Exclusive to Premium Members: YES' : ''}
// ${hasExclusiveFollowing ? '- Exclusive to Followers: YES' : ''}
// ${(hasExclusivePremium || hasExclusiveFollowing) 
//   ? `\nNOTE: This coupon is EXCLUSIVE — mention it clearly in the Description and Terms.`
//   : ''}

// Strategy:
// ${targetSegmentS ? `- Target Audience: ${targetSegmentS}` : ''}
// ${frequencyTargetS ? `- Frequency Goal: ${frequencyTargetS}` : ''}
// - Speed/Vibe: ${speedS}

// GLOBAL RULES:
// OUTPUT: Exactly three labeled sections: "Title:", "Description:", "Terms of Service:".
// NO NULLS: Never mention "no minimum spend" or "no limit." If a constraint is null/empty, stay silent about it.
// Do NOT include quotes around outputs.
// Do NOT add extra labels, bullets, headings, or commentary beyond the three required labels.
// Do NOT invent restrictions, dates, quantities, or prices.
// ${rules ? `Additional rules: ${rules}` : ''}

// =========================================
// SECTION 1: TITLE
// Constraint: MAX 36 CHARACTERS. Single line. No trailing punctuation.

// This is Title ${variantIndex || '—'} of 3.

// TITLE ANGLE (PRIMARY DIRECTIVE - MUST FOLLOW):
// ${titleAngle ? titleAngle : 'Create a compelling, benefit-focused title'}

// CRITICAL VARIETY RULES (ABSOLUTE REQUIREMENTS):
// 1. Your title MUST be distinctly different from the other ${variantIndex === '1' ? 'upcoming' : 'previous'} titles in this set
// 2. Do NOT start with the same first word as any other title
// 3. FORBIDDEN generic starters: Deal, Offer, Special, Save, Discount, Get, Exclusive, Limited
// ${avoidanceContext  ? `4. ADDITIONALLY FORBIDDEN: ${avoidanceContext }` : ''}
// 5. If you cannot follow the title angle within 36 chars, prioritize the character limit and variety rules

// TITLE LOGIC:
// - Focus on the ${itemNameS || 'product'} or the "Vibe" (the experience)
// - THE EXPERIENCE: "Crispy Chicken Sandwich", "Date Night Combo", "Late Night Wings"
// ${singleDayS ? `- Single Day Detected: You MAY use "${singleDayS}" (e.g., "Taco Tuesday Special")` : ''}
// - IF Speed is "Max": Use high-energy words (e.g., "Flash", "Rush", "Blitz")
// - IF Speed is "Steady": Use descriptive, inviting words (e.g., "Classic", "Perfect", "Signature")
// - IF Discount is exceptionally high (Free, 50%+ off): You MAY lead with the value (e.g., "Buy 1 Get 1 Free")

// TITLE EXAMPLES:
// - "Family Feast Combo" (Focus: Product/Family)
// - "Late Night Wings" (Focus: Experience/Time)
// - "Tuesday Pasta Night" (Focus: Single Day Event)
// - "Buy 1 Get 1 Free" (Focus: High Value)

// NEGATIVE CONSTRAINTS (DO NOT USE):
// - Generic Fluff: "Discount Offer", "Save Now", "Special Deal"
// - Technical Jargon: "Valid Mon-Wed" (Instead use: "Midweek Treat")
// - Do NOT mention exclusivity in the title
// - Do NOT repeat or closely mirror any previous title in this set

// =========================================
// SECTION 2: DESCRIPTION
// Constraint: MAX 1000 CHARACTERS.

// OBJECTIVE:
// Sell the experience and the product first. The discount is the cherry on top.
// Write a cohesive paragraph that makes the user feel something (e.g., hungry, excited, relieved).

// TONE STRATEGY:
// CORE VOICE (Default): Authentic, Approachable, and Local. Use sensory details from the item description to make it sound appetizing/inviting.

// ADAPTATION (Only if target audience is specified):
// ${targetSegmentS ? `Shift the narrative angle to suit ${targetSegmentS} (e.g., focus on "convenience" for Families, or "affordability" for Students).` : 'Stay in the Core Voice.'}

// ${exclusivityContext ? `IMPORTANT: Mention exclusivity naturally: "${exclusivityContext}"` : ''}
// ${frequencyContext ? `Weave in frequency messaging: "${frequencyContext}"` : ''}

// STRUCTURE:
// Part 1: The Hook. Describe the ${itemNameS || 'product'} using sensory or benefit-driven language.
// Part 2: The Deal. Naturally integrate the value (e.g., "Enjoy our signature burger for just ${showRegularPrice ? regularPriceS : 'a great price'}").
// Part 3: If conditions exist, blend them into the narrative as positive "nudges" (scarcity, planning, social proof).
//    - If all conditions are null/empty, omit this sentence and end on a high note.

// DO NOT:
// - Use robotic "sales" phrases ("Buy now!", "Don't miss out!")
// - Invent restrictions that don't exist
// - Mention null constraints (e.g., never say "No minimum spend required")

// =========================================
// SECTION 3: TERMS OF SERVICE
// Constraint: ONE SINGLE PARAGRAPH. MAX 580 CHARACTERS.

// OBJECTIVE: Legal validity only. Do not restrict validity to the segment.

// MANDATORY INCLUSION (Combine smoothly):
// "Valid for ${itemNameS || 'specified items'} at ${locationS} only."
// ${hasExclusivePremium ? '"Valid for Premium Members only."' : ''}
// ${hasExclusiveFollowing ? `"Valid for customers who follow ${businessNameS} only."` : ''}
// ${minSpendS ? `"Min. spend $${minSpendS} before tax."` : ''}
// ${purchaseQtyS ? `"Purchase of ${purchaseQtyS} items required."` : ''}
// ${slowDaysS ? `"Valid on: ${slowDaysS}${timeRangeS ? `, ${timeRangeS}` : ''}."` : timeRangeS ? `"Valid ${timeRangeS}."` : ''}
// ${reservationHoursS ? `"Must redeem within ${reservationHoursS} hours of reservation."` : ''}

// REDEMPTION: "Present in-app for redemption. Limit 1 per visit."

// BOILERPLATE (Append exactly):
// "No cash value. Cannot combine with other offers. Subject to availability. Business reserves right to modify offer. See store for details. Further conditions may apply, please visit store for more details."

// =========================================
// NOW PRODUCE THE OUTPUT IN THE EXACT THREE-LABEL FORMAT.
// `.trim();
// };

// /* -------------------- IMAGE (HERO) -------------------- */
// /**
//  * Image suggestion / generation
//  * Old call: logoImagePrompt({ businessName, vibes })
//  * Richer optional: { itemName, categoryMain, categorySub, visualReferenceProvided, desiredImpact }
//  * Returns a concise prompt for your AI image route (500x500 focus).
//  */
// export const logoImagePrompt = (ctx = {}) => {
//   const {
//     businessName = 'Your Business',
//     vibes = 'simple, modern, flat, high-contrast',

//     // richer (optional)
//     itemName = '',
//     categoryMain = '',
//     categorySub = '',
//     visualReferenceProvided = false,
//     desiredImpact = 'Steady',
//   } = ctx;

//   const mood =
//     desiredImpact === 'Max'
//       ? 'bold, high-energy, eye-catching, dynamic lighting'
//       : desiredImpact === 'Quick'
//       ? 'appealing, lively, clear focal point, modern'
//       : 'calm, authentic, high-quality, inviting';

//   // We keep the name for backward compatibility but steer toward a hero image brief.
//   return `
// Create a compelling 500x500 hero image for a coupon (no text in the image).
// Focus on the experience/benefit rather than a flat logo.

// Context:
// - Business: ${businessName}
// - Offering: ${short(itemName) || 'Storewide promotion'}
// - Category: ${short(categoryMain)} / ${short(categorySub)}
// - Visual reference provided by user: ${visualReferenceProvided ? 'Yes' : 'No'}
// - Desired mood: ${mood}
// - Style hints: ${vibes}

// Return ONLY a concise scene description suitable for an AI image generator.`.trim();
// };
